import { useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { Button } from 'src/components';
import {
  chatMessagesAtom,
  chatSessionIdAtom,
  chatSessionOriginAtom,
  chatStreamingAtom,
} from 'src/shared/chat';
import { chatActionModeOverrideAtom } from 'src/shared/chatPreferences';
import { MessageList } from 'src/chat/MessageList';
import { useChatSend } from 'src/chat/useChatSend';
import { useChatAutoSave } from 'src/chat/ChatSidebar';
import { useUpdateOnboardingProgressMutation } from 'src/api/mutations/onboardingMutations';
import { useBudgetAllowed } from 'src/chat/BudgetIndicator';
import MCPConnectGuide from './MCPConnectGuide';
import { buildInAppSuggestedPrompt } from './RunPromptCard';
import styles from './styles.module.scss';
import inAppStyles from './InAppAgentRun.module.scss';

type InAppAgentRunProps = {
  userEmail?: string;
  onComplete?: () => void;
  onClose?: () => void;
};

const InAppAgentRun = ({ userEmail, onComplete, onClose }: InAppAgentRunProps) => {
  const initialPrompt = useMemo(() => buildInAppSuggestedPrompt(userEmail), [userEmail]);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [hasRun, setHasRun] = useState(false);
  const [showMcp, setShowMcp] = useState(false);
  const [completedFired, setCompletedFired] = useState(false);

  const messages = useAtomValue(chatMessagesAtom);
  const isStreaming = useAtomValue(chatStreamingAtom);
  const [, setSessionId] = useAtom(chatSessionIdAtom);
  const [, setSessionOrigin] = useAtom(chatSessionOriginAtom);
  const setActionModeOverride = useUpdateAtom(chatActionModeOverrideAtom);
  const { send: sendChat, stop: stopChat } = useChatSend();
  const budgetAllowed = useBudgetAllowed();
  const progressMutation = useUpdateOnboardingProgressMutation();

  // Persist the session via the same auto-save the chat sidebar uses, so the
  // conversation is available when the user opens the sidebar later.
  useChatAutoSave();

  // Mark the chat as starting fresh from onboarding so we don't carry over
  // a previous session id from another tab.
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    setSessionId(null);
    setSessionOrigin('onboarding');
  }, [setSessionId, setSessionOrigin]);

  // Force 'toast' action mode while onboarding so a tool's `navigate` action
  // doesn't yank the user out of `/signup/:code` mid-run. The user's
  // persisted preference is untouched.
  useEffect(() => {
    setActionModeOverride('toast');
    return () => setActionModeOverride(null);
  }, [setActionModeOverride]);

  const lastMessage = messages[messages.length - 1];
  const lastIsAssistant = lastMessage?.role === 'assistant';
  const completedSuccessfully =
    hasRun && lastIsAssistant && !isStreaming && !lastMessage?.id?.endsWith('-err');

  useEffect(() => {
    if (completedSuccessfully && !completedFired) {
      setCompletedFired(true);
      progressMutation.mutate({ checklist: { ai_workflow_completed: true } });
      onComplete?.();
    }
  }, [completedSuccessfully, completedFired, onComplete, progressMutation]);

  const handleRun = async () => {
    if (!prompt.trim() || isStreaming) return;
    setHasRun(true);
    await sendChat(prompt.trim());
  };

  const runDisabled = !prompt.trim() || isStreaming || !budgetAllowed;

  return (
    <div className={styles.guideContainer}>
      <div className={styles.guideHeader}>
        <h2>Run your first AI Agent task</h2>
        <p>
          We'll run this prompt against the sample assets we just added. Edit it if you like, then
          press Run and watch the agent search, share, and email in real time.
        </p>
      </div>

      {!hasRun && (
        <div className={inAppStyles.promptComposer}>
          <label className={inAppStyles.promptLabel} htmlFor="onboarding-agent-prompt">
            Suggested prompt
          </label>
          <textarea
            id="onboarding-agent-prompt"
            className={inAppStyles.promptTextarea}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            disabled={isStreaming}
          />
          <div className={inAppStyles.promptActions}>
            <Button onClick={handleRun} disabled={runDisabled} loading={isStreaming}>
              Run prompt
            </Button>
            {!budgetAllowed && (
              <span className={inAppStyles.budgetWarning}>
                Credits are exhausted for this account. Try the MCP path below.
              </span>
            )}
          </div>
        </div>
      )}

      {hasRun && (
        <div className={inAppStyles.runArea}>
          <div className={inAppStyles.messagesPane}>
            <MessageList />
          </div>
          <div className={inAppStyles.runFooter}>
            {isStreaming ? (
              <div className={styles.guideCta}>
                <span className={inAppStyles.streamingHint}>
                  The agent is running… you can keep this open or come back later.
                </span>
                <Button mode="tertiary" onClick={stopChat}>
                  Stop
                </Button>
              </div>
            ) : completedSuccessfully ? (
              <div className={styles.guideCta}>
                <Button onClick={onClose}>Continue onboarding</Button>
              </div>
            ) : (
              <div className={styles.guideCta}>
                <Button onClick={handleRun} disabled={runDisabled}>
                  Run again
                </Button>
                <Button mode="tertiary" onClick={onClose}>
                  Skip
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className={styles.toggleLink}
        onClick={() => setShowMcp(!showMcp)}
      >
        {showMcp ? 'Hide external AI client setup' : 'Also connect Claude, ChatGPT, or Mistral'}
      </button>

      {showMcp && (
        <div className={inAppStyles.mcpEmbed}>
          <MCPConnectGuide
            onConnected={() => setShowMcp(false)}
            title="Connect an external AI agent"
            description="Optional. Set up MCP so you can also use FileSpin from Claude, ChatGPT, or any MCP-compatible client."
          />
        </div>
      )}
    </div>
  );
};

export default InAppAgentRun;
